import moment from 'moment';
// import { ValidationErrors } from 'ngrx-forms';
import { equalTo, EqualToValidationError } from 'ngrx-forms/validation';
import { mergeDateAndTime } from '@ellf/common-utils';

export interface DateLessThanError {
  comparand: string;
  actual: string;
  allowEqual: boolean;
}
export interface DateGreaterThanError {
  comparand: string;
  actual: string;
  allowEqual: boolean;
}

export interface TimeLessThanError {
  comparand: string;
  actual: string;
  allowEqual: boolean;
}
export interface TimeGreaterThanError {
  comparand: string;
  actual: string;
  allowEqual: boolean;
}

export interface EqualToError<T> extends EqualToValidationError<T> {
  field: string;
  compareField: string;
}
// declare module 'ngrx-forms/src/state' {
export interface ValidationErrors {
  dateLessThan?: DateLessThanError;
  dateGreaterThan?: DateGreaterThanError;
  timeLessThan?: TimeLessThanError;
  timeGreaterThan?: TimeGreaterThanError;
  fieldEqualTo?: EqualToError<string>;
  json?: boolean;
  jsonSchema?: boolean;
  fieldChecked?: boolean;
  requiredBoolean?: boolean;
}
// }

export function json(value: string) {
  const err = {
    json: true,
  };

  let valid = true;
  if (value) {
    try {
      JSON.parse(value);
    } catch {
      valid = false;
    }
  }

  if (!valid) {
    return err;
  }

  return {};
}

// export function jsonWithSchema(value: string, schema: Record<string, unknown>) {
//   const err = {
//     jsonSchema: true,
//   };

//   let valid = true;
//   if (value) {
//     try {
//       const data = JSON.parse(value);
//       if (schema) {
//         const validator = new ajv({ allErrors: true, verbose: true }); // options can be passed, e.g. {allErrors: true}
//         const validate = validator.compile(schema);
//         valid = validate(data) as boolean;
//       }
//     } catch {
//       valid = false;
//     }
//   }

//   if (!valid) {
//     return err;
//   }

//   return {};
// }

export function dateLessThan(
  comparand: string | null,
  allowEqual = false,
  format = 'L',
): (value: string | null) => ValidationErrors {
  return (value: string | null): ValidationErrors => {
    const err = {
      dateLessThan: {
        comparand: moment(comparand).format(format),
        actual: moment(value).format(format),
        allowEqual,
      },
    };

    let valid = true;
    if (value && comparand) {
      const diff = moment(value)
        .startOf('d')
        .diff(moment(comparand).startOf('d'));
      if (diff < 0) {
        valid = true;
      } else if (diff === 0) {
        valid = allowEqual;
      } else {
        valid = false;
      }
    }
    if (!valid) {
      return err;
    }
    return {};
  };
}

export function timeLessThan(
  comparand: string | null,
  allowEqual = false,
  format = 'LT',
): (value: string | null) => ValidationErrors {
  return (value: string | null): ValidationErrors => {
    const err = {
      timeLessThan: {
        comparand: moment(comparand).format(format),
        actual: moment(value).format(format),
        allowEqual,
      },
    };

    let valid = true;
    if (value && comparand) {
      const timeValue = mergeDateAndTime(
        moment().startOf('d').toISOString(),
        value,
      );
      const timeComparandValue = mergeDateAndTime(
        moment().startOf('d').toISOString(),
        comparand,
      );

      const diff = moment(timeValue).diff(moment(timeComparandValue));
      if (diff < 0) {
        valid = true;
      } else if (diff === 0) {
        valid = allowEqual;
      } else {
        valid = false;
      }
    }
    if (!valid) {
      return err;
    }
    return {};
  };
}

export function dateGreaterThan(
  comparand: string | null,
  allowEqual = false,
  format = 'L',
  withTime = false,
): (value: string | null) => ValidationErrors {
  return (value: string | null): ValidationErrors => {
    const err = {
      dateGreaterThan: {
        comparand: moment(comparand).format(format),
        actual: moment(value).format(format),
        allowEqual,
        withTime,
      },
    };

    let valid = true;
    if (value && comparand) {
      const diff = withTime
        ? moment(value).diff(moment(comparand))
        : moment(value).startOf('d').diff(moment(comparand).startOf('d'));
      if (diff > 0) {
        valid = true;
      } else if (diff === 0) {
        valid = allowEqual;
      } else {
        valid = false;
      }
    }
    if (!valid) {
      return err;
    }
    return {};
  };
}

export function timeGreaterThan(
  comparand: string | null,
  allowEqual = false,
  format = 'LT',
): (value: string | null) => ValidationErrors {
  return (value: string | null): ValidationErrors => {
    const err = {
      timeGreaterThan: {
        comparand: moment(comparand).format(format),
        actual: moment(value).format(format),
        allowEqual,
      },
    };

    let valid = true;
    if (value && comparand) {
      const timeValue = mergeDateAndTime(
        moment().startOf('d').toISOString(),
        value,
      );
      const timeComparandValue = mergeDateAndTime(
        moment().startOf('d').toISOString(),
        comparand,
      );

      const diff = moment(timeValue).diff(moment(timeComparandValue));
      if (diff > 0) {
        valid = true;
      } else if (diff === 0) {
        valid = allowEqual;
      } else {
        valid = false;
      }
    }
    if (!valid) {
      return err;
    }
    return {};
  };
}

export function fieldEqualTo(
  comparand: string,
  field: string,
  compareField: string,
): (value: string) => ValidationErrors {
  return (value: string): ValidationErrors => {
    const equalToErr = equalTo(comparand)(value);
    if (equalToErr && equalToErr.equalTo) {
      return {
        fieldEqualTo: { ...equalToErr.equalTo, field, compareField },
      };
    }
    return {};
  };
}

export function fieldChecked(
  value: boolean | null | undefined,
): ValidationErrors {
  // Se il valore è strettamente true, non ci sono errori
  if (value === true) {
    return {};
  }

  // Altrimenti restituisce l'errore
  return {
    fieldChecked: true,
  };
}
export function requiredBoolean(
  value: boolean | null | undefined,
): ValidationErrors {
  // Se il valore è strettamente true, non ci sono errori
  if (value === true || value === false) {
    return {};
  }

  // Altrimenti restituisce l'errore
  return {
    requiredBoolean: true,
  };
}

export const alphabeticalRegex = /^[A-Z]+$/i;
export const alphabeticalAndSpaceRegex = /^[a-zA-Z ]*$/;
export const alphabeticalAndSpacesAndDashesRegex = /^[a-zA-Z -]*$/;
export const capRegex = /^[0-9]{5}$/;
export const fiscalCodeRegex =
  /^(?:[A-Z][AEIOU][AEIOUX]|[AEIOU]X{2}|[B-DF-HJ-NP-TV-Z]{2}[A-Z]){2}(?:[\dLMNP-V]{2}(?:[A-EHLMPR-T](?:[04LQ][1-9MNP-V]|[15MR][\dLMNP-V]|[26NS][0-8LMNP-U])|[DHPS][37PT][0L]|[ACELMRT][37PT][01LM]|[AC-EHLMPR-T][26NS][9V])|(?:[02468LNQSU][048LQU]|[13579MPRTV][26NS])B[26NS][9V])(?:[A-MZ][1-9MNP-V][\dLMNP-V]{2}|[A-M][0L](?:[1-9MNP-V][\dLMNP-V]|[0L][1-9MNP-V]))[A-Z]$/i;
export const phoneNumberRegex =
  /^\+?\d{1,4}?[-.\s]?\(?\d{1,3}?\)?[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/;
export const partitaIvaRegex = /^[0-9]{11}$/;
export const multiplEmailRegex =
  // eslint-disable-next-line no-useless-escape
  /^([a-z0-9,!#\$%&'\*\+/=\?\^_`\{\|}~-]+(\.[a-z0-9,!#\$%&'\*\+/=\?\^_`\{\|}~-]+)*@[a-z0-9-]+(\.[a-z0-9-]+)*\.([a-z]{2,})){1}(;[a-z0-9,!#\$%&'\*\+/=\?\^_`\{\|}~-]+(\.[a-z0-9,!#\$%&'\*\+/=\?\^_`\{\|}~-]+)*@[a-z0-9-]+(\.[a-z0-9-]+)*\.([a-z]{2,}))*$/;

export const concatRegex = (regex1: RegExp, regex2: RegExp) => {
  const flags = (regex1.flags + regex2.flags)
    .split('')
    .sort()
    .join('')
    .replace(/(.)(?=.*\1)/g, '');
  return new RegExp(regex1.source + regex2.source, flags);
};
